import { useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  Field,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  Option,
  Textarea,
} from "@fluentui/react-components";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Activity, ContextBlock, Decision, InputField } from "../types";
import styles from "./ApprovalsPanel.module.css";

/** Stable key for a field's value in the collected `inputs`. */
const fieldKey = (field: InputField, index: number) => field.key ?? field.label ?? `field_${index}`;

/** One context block → its Fluent rendering, switched on `kind`. */
function ContextBlockView({ block }: { readonly block: ContextBlock }) {
  switch (block.kind) {
    case "summary":
      return (
        <div className={styles.summary}>
          <Markdown remarkPlugins={[remarkGfm]}>{block.text ?? ""}</Markdown>
        </div>
      );
    case "facts":
      return (
        <dl className={styles.facts}>
          {(block.items ?? []).map((item) => (
            <div key={`${item.label ?? ""}=${item.value ?? ""}`} style={{ display: "contents" }}>
              <dt className={styles.factLabel}>{item.label}</dt>
              <dd className={styles.factValue} style={{ margin: 0 }}>
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      );
    case "draft":
      return (
        <div className={styles.draft}>
          {block.title && <p className={styles.draftTitle}>{block.title}</p>}
          {block.content}
        </div>
      );
    case "links":
      return (
        <div className={styles.links}>
          {(block.items ?? []).map((item) => (
            <Link key={item.url ?? item.label} href={item.url} target="_blank" rel="noreferrer">
              {item.label ?? item.url}
            </Link>
          ))}
        </div>
      );
    case "note":
      return (
        <MessageBar intent={block.severity === "warning" ? "warning" : "info"}>
          <MessageBarBody>{block.text}</MessageBarBody>
        </MessageBar>
      );
    default:
      return null;
  }
}

/** One input field → its Fluent control. Native input types cover number/date/
 *  email/file without pulling in extra packages; select uses a Dropdown. */
function InputFieldControl({
  field,
  value,
  disabled,
  onChange,
}: {
  readonly field: InputField;
  readonly value: unknown;
  readonly disabled: boolean;
  readonly onChange: (v: unknown) => void;
}) {
  // Form values are primitives (strings from the controls); guard so a stray
  // object renders as its JSON, never the useless "[object Object]".
  let str: string;
  if (value == null) {
    str = "";
  } else if (typeof value === "object") {
    str = JSON.stringify(value);
  } else {
    str = String(value as string | number | boolean | bigint);
  }
  let control;
  if (field.type === "textarea") {
    control = (
      <Textarea
        value={str}
        placeholder={field.placeholder}
        disabled={disabled}
        onChange={(_, d) => onChange(d.value)}
      />
    );
  } else if (field.type === "select") {
    control = (
      <Dropdown
        placeholder={field.placeholder ?? "Select…"}
        disabled={disabled}
        value={str}
        selectedOptions={str ? [str] : []}
        onOptionSelect={(_, d) => onChange(d.optionValue)}
      >
        {(field.options ?? []).map((opt) => (
          <Option key={opt} value={opt}>
            {opt}
          </Option>
        ))}
      </Dropdown>
    );
  } else if (field.type === "file") {
    // Fluent's Input has no type="file"; capture the chosen file's name only
    // (v1 collects a reference, not the binary).
    control = (
      <input
        type="file"
        disabled={disabled}
        onChange={(e) => onChange(e.target.files?.[0]?.name ?? "")}
      />
    );
  } else {
    // text | number | date | email → Fluent Input with a native input type
    const type = (["number", "date", "email"].includes(field.type) ? field.type : "text") as
      | "text"
      | "number"
      | "date"
      | "email";
    control = (
      <Input
        type={type}
        value={str}
        placeholder={field.placeholder}
        disabled={disabled}
        onChange={(_, d) => onChange(d.value)}
      />
    );
  }
  return (
    <Field
      label={field.label}
      required={field.required}
      hint={field.type === "file" ? "Captures the file reference only." : undefined}
    >
      {control}
    </Field>
  );
}

interface ActivityCardProps {
  readonly activity: Activity;
  readonly submitting: boolean;
  readonly onSubmit: (decision: Decision) => void;
}

/** A single pending decision: the prompt, a rich context panel, an adaptive
 *  input form, an optional feedback box, and the decision controls (one button
 *  per option, or a single Submit for open-ended gates). */
export function ActivityCard({ activity, submitting, onSubmit }: ActivityCardProps) {
  const [feedback, setFeedback] = useState("");
  const [values, setValues] = useState<Record<string, unknown>>({});

  const fields = activity.input_fields ?? [];
  const context = activity.context ?? [];
  const openEnded = activity.options.length === 0;
  const options = openEnded ? [] : activity.options.map(String);

  const hasValue = (v: unknown): boolean => {
    if (v == null) return false;
    // Only primitives are real answers; an object is treated as empty rather
    // than coerced to "[object Object]".
    if (typeof v === "object") return false;
    const primitive = v as string | number | boolean | bigint;
    return String(primitive).trim() !== "";
  };
  const formValid = useMemo(
    () => fields.every((f, i) => !f.required || hasValue(values[fieldKey(f, i)])),
    [fields, values],
  );

  const collectInputs = () =>
    fields.reduce<Record<string, unknown>>((acc, f, i) => {
      const key = fieldKey(f, i);
      if (hasValue(values[key])) acc[key] = values[key];
      return acc;
    }, {});

  const submit = (decision: string) =>
    onSubmit({
      decision,
      feedback: feedback.trim() || undefined,
      inputs: fields.length > 0 ? collectInputs() : undefined,
    });

  const disabled = submitting || !formValid;

  return (
    <li className={styles.card}>
      <p className={styles.prompt}>{activity.prompt}</p>
      <p className={styles.meta}>{activity.skill_key}</p>

      {context.length > 0 && (
        <div className={styles.context}>
          {context.map((block) => (
            <ContextBlockView
              key={`${block.kind}:${block.title ?? block.text ?? block.content ?? ""}`}
              block={block}
            />
          ))}
        </div>
      )}

      {fields.length > 0 && (
        <div className={styles.form}>
          {fields.map((field, i) => (
            <InputFieldControl
              key={fieldKey(field, i)}
              field={field}
              value={values[fieldKey(field, i)]}
              disabled={submitting}
              onChange={(v) => setValues((prev) => ({ ...prev, [fieldKey(field, i)]: v }))}
            />
          ))}
        </div>
      )}

      <Textarea
        className={styles.feedback}
        placeholder="Optional feedback…"
        value={feedback}
        disabled={submitting}
        onChange={(_, data) => setFeedback(data.value)}
        aria-label="Feedback"
      />

      <div className={styles.actions}>
        {openEnded ? (
          <Button appearance="primary" disabled={disabled} onClick={() => submit(feedback.trim() || "submitted")}>
            Submit
          </Button>
        ) : (
          options.map((option, index) => (
            <Button
              key={option}
              appearance={index === 0 ? "primary" : "secondary"}
              disabled={disabled}
              onClick={() => submit(option)}
            >
              {option}
            </Button>
          ))
        )}
      </div>
    </li>
  );
}
